import {Component, Plugins} from '@packages/systems/wfdl';
import type {GlobalComponents} from '@packages/systems/global-component-settings';
import {globalComponentsSettingsSaveRequested} from '@packages/systems/global-component-settings';
import type {DesignerFlux} from '@packages/systems/designer/flux';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';

/**
 * Auto-populates global component settings for sites that have never had them configured.
 *
 * The value of `globalComponentSettings.components` defaults to `null` and only becomes an array when:
 *   - A user saves global component settings via the UI
 *   - This initialization logic has already run
 */
export function initializeGlobalComponents(
  flux: DesignerFlux,
  remoteData: {globalComponentSettings?: {components?: unknown}}
): void {
  const state = flux.getState();

  // First, checks that the user has access to global component settings.
  // If not, any changes produced by the initialization flow will be
  // rejected the API, so we can skip making any requests.
  const canAccessGlobalComponentSettings = selectDesignerAbility(
    'canAccessGlobalComponentSettings'
  )(state);
  if (!canAccessGlobalComponentSettings) {
    return;
  }

  const rawGlobalComponents = remoteData.globalComponentSettings?.components;

  // Next, checks if the remote global component data is already an array.
  // If so, these settings have already been initialized and no further action is needed.
  if (Array.isArray(rawGlobalComponents)) {
    return;
  }

  // Finally, derive what components are considered "global" via `deriveInitialGlobalComponents`.
  // For any matches, a `GlobalComponentEntry` is pushed to the list and saved to site settings.
  // When there are no matching components, an empty array will be set instead, which enables
  // returning early in the step above on subsequent designer loads.
  const components = deriveInitialGlobalComponents(
    state.DesignerStore.components
  );

  flux.dispatch(globalComponentsSettingsSaveRequested({components}));
}

const sitePluginName = Plugins.name('__SitePlugin');

const SCOPE_PATTERN = /\b(global|site)\b/i;
const STYLE_PATTERN = /\b(styles?|css)\b/i;

/**
 * Scans the component map for user components whose display name contains both
 * a scope keyword ("global" or "site") and a style keyword ("style", "styles", or "css"),
 * case-insensitive with word-boundary matching. Returns matches as an ordered array
 * of global component entries.
 */
export function deriveInitialGlobalComponents(
  componentMap: Component.ComponentMap
): GlobalComponents {
  const result: GlobalComponents = [];

  componentMap.reduceAtPluginName(
    sitePluginName,
    (acc, component, componentName) => {
      if (componentName === 'page') {
        return acc;
      }

      const name = (Component.getDisplayName(component) ?? '').trim();
      if (!name) return acc;

      if (SCOPE_PATTERN.test(name) && STYLE_PATTERN.test(name)) {
        acc.push({id: componentName});
      }

      return acc;
    },
    result
  );

  return result;
}
